import React from 'react'

import { Box, Skeleton } from '@mui/material'

import EnterpriseReportBox from './EnterpriseReportBox'
import styles from './UsageSection.module.scss'
import UsageTable from './UsageTable'
import UsageTableFooter from './UsageTableFooter'
import { formatPercentage } from '../../../utils/formatUtils'

interface UsageSectionProps {
  loading: boolean
  checklistsUsed?: {
    total: number
    userChecklistsUsedPerPlatform?: Array<{
      platform: string
      num: number
    } | null>
    percentageOfUsersWithChecklistPerPlatform?: Array<{
      platform: string
      num: number
    } | null>
    percentageOfUsersWithChecklistAnywhere: number
  } | null
  recommendationsUpdated: {
    total: number
    recommendationsUpdatedPerPlatform?: Array<{
      platform: string
      num: number
    } | null>
    percentageOfUsersWithRecommendationsUpdatedAnywhere?: number | null
  }
  contentDeleted: {
    total: number
    userContentDeletedPerPlatform?: Array<{
      platform: string
      num: number
    } | null>
    percentageOfUsersWithContentDeletedAnywhere?: number | null
  }
  connectionsRemoved: {
    total: number
    userConnectionsRemovedPerPlatform?: Array<{
      platform: string
      num: number
    } | null>
    percentageOfUsersWithConnectionsRemovedAnywhere?: number | null
  }
}

function AdoptionHeader({
  title,
  loading,
  count,
}: {
  title: string
  loading: boolean
  count: number
}) {
  return (
    <div className={styles.adoptionHeader}>
      <h2 className={styles.adoptionTitle}>{title}</h2>
      <div className={styles.adoptionCount}>
        {loading ? <Skeleton width={40} /> : count}
      </div>
    </div>
  )
}

export default function UsageSection({
  loading,
  checklistsUsed,
  recommendationsUpdated,
  contentDeleted,
  connectionsRemoved,
}: UsageSectionProps) {
  return (
    <Box className={styles.usageGrid}>
      {/* Checklists used */}
      <EnterpriseReportBox
        header={
          <AdoptionHeader
            title="📋 Checklists used"
            loading={loading}
            count={checklistsUsed?.total}
          />
        }
        content={
          <UsageTable
            loading={loading}
            platformData={checklistsUsed?.userChecklistsUsedPerPlatform.map(
              (item) => ({
                platform: item.platform,
                total: item.num,
                pct: checklistsUsed?.percentageOfUsersWithChecklistPerPlatform.find(
                  (p) => p.platform === item.platform,
                )?.num,
              }),
            )}
            columns={[
              { key: 'total', label: 'Total' },
              { key: 'pct', label: 'Pct. of users', format: formatPercentage },
            ]}
          />
        }
        footer={
          <UsageTableFooter
            overallLabel="Used at least 1 checklist"
            overallValue={formatPercentage(
              checklistsUsed?.percentageOfUsersWithChecklistAnywhere,
            )}
          />
        }
      />

      {/* Settings updated */}
      <EnterpriseReportBox
        header={
          <AdoptionHeader
            title="⚙️ Settings updated"
            loading={loading}
            count={recommendationsUpdated?.total || 0}
          />
        }
        content={
          <UsageTable
            loading={loading}
            platformData={recommendationsUpdated?.recommendationsUpdatedPerPlatform.map(
              (item) => ({
                platform: item.platform,
                total: item.num,
              }),
            )}
            columns={[{ key: 'total', label: 'Total' }]}
          />
        }
        footer={
          <UsageTableFooter
            overallLabel="Updated at least 1 setting"
            overallValue={formatPercentage(
              recommendationsUpdated?.percentageOfUsersWithRecommendationsUpdatedAnywhere,
            )}
          />
        }
      />

      {/* Content deleted */}
      <EnterpriseReportBox
        header={
          <AdoptionHeader
            title="🗑️ Content removed"
            loading={loading}
            count={contentDeleted?.total || 0}
          />
        }
        content={
          <UsageTable
            loading={loading}
            platformData={contentDeleted?.userContentDeletedPerPlatform.map(
              (item) => ({
                platform: item?.platform || '',
                total: item?.num ? item.num.toLocaleString() : null,
              }),
            )}
            columns={[{ key: 'total', label: 'Total' }]}
          />
        }
        footer={
          <UsageTableFooter
            overallLabel="Removed at least 1 piece of content"
            overallValue={formatPercentage(
              contentDeleted?.percentageOfUsersWithContentDeletedAnywhere,
            )}
          />
        }
      />

      {/* Connections removed */}
      <EnterpriseReportBox
        header={
          <AdoptionHeader
            title="🔗 Connections removed"
            loading={loading}
            count={connectionsRemoved?.total || 0}
          />
        }
        content={
          <UsageTable
            loading={loading}
            platformData={connectionsRemoved?.userConnectionsRemovedPerPlatform.map(
              (item) => ({
                platform: item?.platform || '',
                total: item?.num ? item.num.toLocaleString() : null,
              }),
            )}
            columns={[{ key: 'total', label: 'Total' }]}
          />
        }
        footer={
          <UsageTableFooter
            overallLabel="Removed at least 1 connection"
            overallValue={formatPercentage(
              connectionsRemoved?.percentageOfUsersWithConnectionsRemovedAnywhere,
            )}
          />
        }
      />
    </Box>
  )
}
